/**
 * Who still owes the game's first move — the ONE home of that rule, shared by
 * the reducer (`playerTurnResponse`'s `firstMoveDeadlineMs`) and the Rail's
 * dock (`pickDockEvent`'s first-move countdown), so the two can never disagree.
 *
 * The server aborts the game if EITHER side misses its first move, so the
 * cosmetic window belongs to whoever has not moved yet — and that is decided by
 * the SIDE TO MOVE IN THE START POSITION, not by the colour: a custom start FEN
 * with 'b' to move (`isBlackTurnFirst`) makes BLACK the one who owes ply 1 and
 * white the one who owes ply 2. Old refs: PlayChess L2671-2676 (the showAbort
 * window is the same "before my own first move" rule) and L2558-2620.
 */

/** how many plies have been played, plus who I am in this start position */
export type FirstMoveInput = {
  moves: number;
  myColor: 'white' | 'black' | null;
  isBlackTurnFirst: boolean;
};

/** true when MY colour is the one to move in the start position */
export function myColorMovesFirst(
  input: Pick<FirstMoveInput, 'myColor' | 'isBlackTurnFirst'>,
): boolean {
  return input.isBlackTurnFirst
    ? input.myColor === 'black'
    : input.myColor === 'white';
}

/**
 * The ply MY first move lands on: 1 when I open, 2 when I answer. An unseated
 * player (`myColor === null`) counts as answering, which is what the pre-fix
 * `myColor === 'white' ? 1 : 2` did.
 */
export function myFirstMovePly(
  input: Pick<FirstMoveInput, 'myColor' | 'isBlackTurnFirst'>,
): number {
  return myColorMovesFirst(input) ? 1 : 2;
}

/** true while my own first move is still missing from the move list */
export function owesFirstMove(input: FirstMoveInput): boolean {
  return input.moves < myFirstMovePly(input);
}
